import { BAD, FLAT, GOOD, SEQUENTIAL } from './charts/chartColors.js'
import { LOWER_IS_BETTER } from './labels.js'
import type { FacilityMonthRollup, HeadlineKey, InsightsMarker, PortfolioRow } from './types.js'

/** The pure parts of the Insights screens: how rows are ordered, how a movement reads, and what the rollups add up to for a chart. */

export type Direction = 'up' | 'down' | 'flat'

/** Which way a figure moved, read against the figure's own good direction so coral always means worse. */
export function directionOf(key: HeadlineKey, movement: number | null, threshold = 0): Direction | null {
  if (movement === null) {
    return null
  }
  const better = LOWER_IS_BETTER[key] ? -movement : movement
  return better > threshold ? 'up' : better < -threshold ? 'down' : 'flat'
}

export const DIRECTION_GLYPH: Record<Direction, string> = { up: '▲', down: '▼', flat: '●' }
export const DIRECTION_COLOR: Record<Direction, string> = { up: GOOD, down: BAD, flat: FLAT }

/**
 * The rows with the facility sliding most first - a worsening of the chosen figure sorts to the top - then those whose
 * chosen figure has no movement yet, then those with no month counted at all (a large client's roster-only facilities),
 * each group by name. Before 27 October 2026 a figure every row lacked put all 316 facilities in name order, the empty
 * ones first.
 */
export function sortByMovement(rows: readonly PortfolioRow[], key: HeadlineKey): PortfolioRow[] {
  const signed = (row: PortfolioRow): number | null => {
    const movement = row.movement[key]
    return movement === null ? null : LOWER_IS_BETTER[key] ? -movement : movement
  }
  const group = (row: PortfolioRow): number => (row.latest === null ? 2 : signed(row) === null ? 1 : 0)
  return [...rows].sort((a, b) => {
    const order = group(a) - group(b)
    if (order !== 0) {
      return order
    }
    const left = signed(a)
    const right = signed(b)
    return (left !== null && right !== null ? left - right : 0) || a.name.localeCompare(b.name)
  })
}

/** How many of the rows have no month counted yet. */
export const withoutMonth = (rows: readonly PortfolioRow[]): number => rows.filter((row) => row.latest === null).length

/** The month a figure is as of where that is not the row's latest month, as a short label to set beside it; empty otherwise. */
export function asOfLabel(row: Pick<PortfolioRow, 'latest' | 'asOf'>, key: HeadlineKey, monthLabel: (period: string) => string): string {
  const asOf = row.asOf?.[key] ?? null
  return asOf !== null && asOf !== row.latest ? monthLabel(asOf) : ''
}

/** The five score bands a month's finals fall in, from the rollup's ten bins: below 60, the sixties, seventies, eighties and 90 up. */
export const SCORE_BANDS = ['Below 60', '60 to 69', '70 to 79', '80 to 89', '90 and above'] as const

export function scoreBandsOf(rollup: Pick<FacilityMonthRollup, 'scores'>): number[] {
  const bins = rollup.scores.bins
  const sum = (from: number, to: number): number => bins.slice(from, to).reduce((total, count) => total + count, 0)
  return [sum(0, 6), sum(6, 7), sum(7, 8), sum(8, 9), sum(9, 10)]
}

export const SCORE_BAND_COLORS: readonly string[] = SEQUENTIAL

/** The rollups of every chosen facility added up per month: the bands, for the distribution chart. */
export function bandsByPeriod(rollups: readonly FacilityMonthRollup[], periods: readonly string[]): (number | null)[][] {
  return SCORE_BANDS.map((_, band) =>
    periods.map((period) => {
      const months = rollups.filter((rollup) => rollup.period === period && rollup.scores.count > 0)
      return months.length === 0 ? null : months.reduce((total, rollup) => total + (scoreBandsOf(rollup)[band] ?? 0), 0)
    }),
  )
}

/** A behavior's average value per month over the rollups given (one facility's, or the portfolio's), null where it did not count. */
export function behaviorSeries(rollups: readonly FacilityMonthRollup[], key: string, periods: readonly string[]): (number | null)[] {
  return periods.map((period) => {
    const months = rollups.filter((rollup) => rollup.period === period && rollup.behaviors[key] !== undefined && (rollup.behaviors[key]?.count ?? 0) > 0)
    if (months.length === 0) {
      return null
    }
    const count = months.reduce((total, rollup) => total + (rollup.behaviors[key]?.count ?? 0), 0)
    const sum = months.reduce((total, rollup) => total + (rollup.behaviors[key]?.sum ?? 0), 0)
    return count === 0 ? null : Math.round((sum / count) * 10) / 10
  })
}

/** Every behavior key the rollups carry, with the weight it most recently carried and how many months it counted in. */
export function behaviorsOf(rollups: readonly FacilityMonthRollup[]): { key: string; weight: number | null; months: number }[] {
  const seen = new Map<string, { weight: number | null; months: number; period: string }>()
  for (const rollup of [...rollups].sort((a, b) => a.period.localeCompare(b.period))) {
    for (const [key, tally] of Object.entries(rollup.behaviors)) {
      if (tally.count === 0) {
        continue
      }
      const current = seen.get(key)
      const weight = Math.round((tally.weightSum / tally.count) * 10) / 10
      seen.set(key, { weight, months: (current?.months ?? 0) + 1, period: rollup.period })
    }
  }
  return [...seen.entries()].map(([key, entry]) => ({ key, weight: entry.weight, months: entry.months })).sort((a, b) => (b.weight ?? 0) - (a.weight ?? 0) || a.key.localeCompare(b.key))
}

/** The lowest and highest facility's average score per month, the band a portfolio trend is drawn in. */
export function scoreBand(rows: readonly PortfolioRow[], periods: readonly string[]): { low: (number | null)[]; high: (number | null)[] } {
  const low = periods.map((_, index) => {
    const values = rows.map((row) => row.series.score[index]).filter((value): value is number => value !== null && value !== undefined)
    return values.length === 0 ? null : Math.min(...values)
  })
  const high = periods.map((_, index) => {
    const values = rows.map((row) => row.series.score[index]).filter((value): value is number => value !== null && value !== undefined)
    return values.length === 0 ? null : Math.max(...values)
  })
  return { low, high }
}

/** The month index each marker falls in, for the rule on a trend; a marker outside the span is left out. */
export function markerIndexes(markers: readonly InsightsMarker[], periods: readonly string[]): { index: number; marker: InsightsMarker }[] {
  return markers.flatMap((marker) => {
    const index = periods.indexOf(marker.at.slice(0, 7))
    return index === -1 ? [] : [{ index, marker }]
  })
}
